<template>
  <v-container>
    <v-row>
      <v-col cols="12" md="10" offset-md="1" lg="8" offset-lg="2">
        <v-toolbar>
          <v-toolbar-title>Admin Dashboard</v-toolbar-title>
        </v-toolbar>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <v-card ref="linkCard" style="margin-bottom:1em">
          <v-card-title>Quick Links</v-card-title>
          <v-list>
            <v-list-item to="/admin/sections">
              <v-list-item-title>Section List</v-list-item-title>
            </v-list-item>
            <v-list-item to="/admin/tracker">
              <v-list-item-title>Progress Tracker</v-list-item-title>
            </v-list-item>
            <v-list-item to="/admin/advising/schedule">
              <v-list-item-title>Set Advising Schedule</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      <v-col v-if="isAdmin && pieData.data.length > 0" cols="12" md="6">
        <fusioncharts :width="smallWidth" :height="smallHeight" :dataFormat="dataFormat" :dataSource="pieData" type="pie2d"></fusioncharts>
      </v-col>
      <v-col v-if="isAdmin" ref="chartCol" cols="12">
        <v-tabs v-model="tab">
          <v-tab>Overall Status</v-tab>
          <v-tab :disabled="isFamilyTerm">Placement Tests</v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab">
          <v-tab-item>
            <v-card :style="'min-height:' + height + 'px'">
              <fusioncharts v-if="overallData.data.length > 0" type="column2d" :width="width" :height="height" :dataFormat="dataFormat" :dataSource="overallData"></fusioncharts>
              <v-card-text v-else>
                There is currently no student completion data. Please check the selected term and make sure that you have selected one that is active.
              </v-card-text>
            </v-card>
          </v-tab-item>
          <v-tab-item>
            <v-card :style="'min-height:' + height + 'px'">
              <fusioncharts type="stackedcolumn2d" :width="width" :height="height" :dataFormat="dataFormat" :dataSource="testData"></fusioncharts>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import { hasRole } from '../../helpers/has'
import { ref, computed, onMounted, reactive, watch } from '@vue/composition-api'
export default {
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user)
    const isAdmin = computed(() => hasRole(user.value, ['Student Development', 'Technology Services', 'Marketing & Communications', 'Ofc. of Marketing & Communications', 'Communications Dept']))
    const tab = ref(0)

    const sections = ref([])
    const students = ref([])

    const linkCard = ref(null)
    const chartCol = ref(null)

    const smallWidth = ref(100)
    const smallHeight = ref(100)
    const width = ref(550)
    const height = ref(350)
    const testWidth = ref(350)
    const testHeight = ref(250)
    const dataFormat = ref('json')
    const pieData = reactive({
      chart: {
        caption: 'Completed Sections',
        subCaption: 'Number of sections each student completed',
        xAxisName: 'Completed Sections',
        yAxisName: 'Student Count',
        theme: 'fusion',
        showLegend: false
      },
      data: []
    })
    const overallData = reactive({
      chart: {
        caption: 'Overall Status',
        subCaption: 'Last section each student completed',
        xAxisName: 'Completed Sections',
        yAxisName: 'Student Count',
        theme: 'fusion'
      },
      data: []
    })
    const testData = reactive({
      chart: {
        caption: 'Test Status',
        xAxisName: 'Tests',
        yAxisName: 'Student Count',
        theme: 'fusion'
      },
      categories: [
        {
          category: []
        }
      ],
      dataset: [
        {
          seriesName: 'Done',
          data: []
        },
        {
          seriesName: 'Dismissed',
          data: []
        },
        {
          seriesName: 'Pending',
          data: []
        }
      ]
    })

    const term = computed(() => root.$store.state.admin.term)
    watch(term, () => {
      if (term.value != null && term.value !== '') {
        loadData()
      }
    })
    const isFamilyTerm = computed(() => isNaN(term.value))

    onMounted(async () => {
      if (!hasRole(user.value, 'Employee')) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'You do not have access to the admin system.' })
        root.$router.push('/')
        return
      }

      smallWidth.value = linkCard.value.offsetWidth
      smallHeight.value = linkCard.value.offsetHeight
      width.value = chartCol.value.offsetWidth - 24
      loadData()
    })

    const service = root.$feathers.service('orientation/tracker')
    async function loadData () {
      const aggregate = [
        { $match: { term: term.value, isTest: false } },
        { $unwind: { path: '$sections', includeArrayIndex: 'sectionIndex', preserveNullAndEmptyArrays: true } },
        { $match: { 'sections.status': 'Complete' } },
        { $group: { _id: '$pidm', section: { $max: '$sectionIndex' } } },
        { $group: { _id: '$section', count: { $sum: 1 } } },
        { $sort: { _id: 1 } }
      ]
      const { data } = await service.find({ query: { aggregate } })
      const { total } = await service.find({ query: { isTest: false, term: term.value, $or: [{ 'sections.0': { $exists: false } }, { sections: { $not: { $elemMatch: { status: 'Complete' } } } }], $limit: 0 } })
      overallData.data = [{ label: 'Not Started', value: total, toolText: total + ' Students{br}Completed 0 Sections' }]
      pieData.data = [{ label: 'Not Started', value: total }]

      let { data: sectionData } = await root.$feathers.service('orientation/section').find({ query: { term: term.value, active: true, $limit: 50, $sort: { order: 1 } } })
      for (const { _id: index, count: value } of data) {
        if (sectionData.length >= index) {
          overallData.data.push({ label: sectionData[index].code, value, toolText: value + ' Students{br}Completed ' + (index + 1) + ' Section' + (index === 1 ? '' : 's') })
        }
      }
      loadPieData(sectionData)
      loadTestData()
    }
    async function loadPieData (sectionData) {
      // Pie Data is built from the number of sections completed
      const aggregate = [
        { $match: { term: term.value, isTest: false } },
        { $unwind: { path: '$sections', includeArrayIndex: 'sectionIndex', preserveNullAndEmptyArrays: true } },
        { $match: { 'sections.status': 'Complete' } },
        { $group: { _id: '$pidm', section: { $sum: 1 } } },
        { $group: { _id: '$section', count: { $sum: 1 } } },
        { $sort: { _id: 1 } }
      ]
      const { data } = await service.find({ query: { aggregate } })
      for (const { _id: index, count: value } of data) {
        if (index === sectionData.length) {
          pieData.data.push({ label: 'All Complete', value })
        } else {
          pieData.data.push({ label: value + ' Section' + (index === 1 ? '' : 's'), value })
        }
      }
    }
    async function loadTestData () {
      testData.categories[0].category = []
      testData.dataset[0].data = []
      testData.dataset[1].data = []
      testData.dataset[2].data = []

      if (isFamilyTerm.value) {
        return
      }
      const aggregate = [
        { $match: { term: term.value, isTest: false } },
        { $unwind: { path: '$actions' } },
        { $match: { 'actions.link': { $not: { $regex: 'action', $options: 'i' } } } },
        { $group: {
          '_id': '$actions.title',
          'done': {
            $sum: {
              $cond: ['$actions.done', 1, 0]
            }
          },
          'dismissed': {
            $sum: {
              $cond: ['$actions.dismissed', 1, 0]
            }
          },
          'count': { $sum: 1 }
        } }
      ]

      const { data } = await service.find({ query: { aggregate } })
      for (const { _id, done, dismissed, count } of data) {
        testData.categories[0].category.push({ label: _id })
        testData.dataset[0].data.push({ value: done })
        testData.dataset[1].data.push({ value: dismissed })
        testData.dataset[2].data.push({ value: count - (done || 0) - (dismissed || 0) })
      }
    }
    return {
      user,
      isAdmin,
      tab,
      sections,
      students,
      linkCard,
      chartCol,
      smallWidth,
      smallHeight,
      width,
      height,
      testWidth,
      testHeight,
      dataFormat,
      pieData,
      overallData,
      testData,
      term,
      isFamilyTerm
    }
  }
}
</script>
